import * as React from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useAppDispatch, useAppSelector } from '../../../State/Store';
import { fetchSellerOrders, updateOrderStatus } from '../../../State/seller/sellerOrderSlice';
import { Button, Menu, MenuItem, FormControl, InputLabel, Select, Box, SelectChangeEvent, Chip } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const orderStatus = [
  { color: '#FFA500', label: 'PENDING' },
  { color: '#F5BCBA', label: 'CONFIRMED' },
  { color: '#F5BCBA', label: 'PLACED' },
  { color: '#1E90FF', label: 'SHIPPED' },
  { color: '#32CD32', label: 'DELIVERED' },
  { color: '#FF0000', label: 'CANCELLED' },
] as const;

// Helper function to add thousand separators
const formatCurrency = (amount: number) => {
  return amount ? amount.toLocaleString('en-US') : '0';
};

const KithimuLogo = () => (
    <div className="flex items-center gap-2 group cursor-pointer">
        <img
            src="/logo.png"
            alt="Power Tools"
            className="h-9 sm:h-11 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            onError={(e) => {
                (e.target as HTMLElement).style.display = 'none';
            }}
        />
        <span className="text-slate-800 font-extrabold text-lg sm:text-xl tracking-wide uppercase drop-shadow-sm font-sans">
            Power<span className="text-amber-700 font-light ml-1">Tools</span>
        </span>
    </div>
);

export default function OrderTable() {
  const dispatch = useAppDispatch();
  const { sellerOrder } = useAppSelector((store) => store);

  // Time filter state: 'ALL' or '24HRS'
  const [dateFilter, setDateFilter] = React.useState<string>('ALL');

  React.useEffect(() => {
    dispatch(fetchSellerOrders(localStorage.getItem('jwt') || ''));
  }, [dispatch]);

  // Status Menu Anchor State
  const [anchorElStatus, setAnchorElStatus] = React.useState<Record<number, HTMLElement | null>>({});

  // Print Menu Anchor State
  const [anchorElPrint, setAnchorElPrint] = React.useState<Record<number, HTMLElement | null>>({});

  const handleStatusClick = (event: React.MouseEvent<HTMLButtonElement>, orderId: number) => {
    setAnchorElStatus((prev) => ({ ...prev, [orderId]: event.currentTarget }));
  };

  const handleStatusClose = (orderId: number) => () => {
    setAnchorElStatus((prev) => ({ ...prev, [orderId]: null }));
  };

  const handlePrintClick = (event: React.MouseEvent<HTMLButtonElement>, orderId: number) => {
    setAnchorElPrint((prev) => ({ ...prev, [orderId]: event.currentTarget }));
  };

  const handlePrintClose = (orderId: number) => () => {
    setAnchorElPrint((prev) => ({ ...prev, [orderId]: null }));
  };

  const handleUpdateOrderStatus = (orderId: number, statusLabel: any) => () => {
    dispatch(updateOrderStatus({ jwt: localStorage.getItem('jwt') || '', orderId, orderStatus: statusLabel }));
    handleStatusClose(orderId)();
  };

  const handleFilterChange = (event: SelectChangeEvent) => {
    setDateFilter(event.target.value as string);
  };

  // Filter orders by time and sort newest orders first
  const filteredOrders = [...(sellerOrder.orders || [])]
    .filter((order: any) => {
      if (dateFilter === '24HRS') {
        if (!order.orderDate) return false;

        const orderDate = new Date(order.orderDate).getTime();
        const now = Date.now();
        const twentyFourHours = 24 * 60 * 60 * 1000;

        return (
          !Number.isNaN(orderDate) &&
          now - orderDate >= 0 &&
          now - orderDate <= twentyFourHours
        );
      }

      return true;
    })
    .sort((a: any, b: any) => {
      const dateA = a.orderDate
        ? new Date(a.orderDate).getTime()
        : 0;

      const dateB = b.orderDate
        ? new Date(b.orderDate).getTime()
        : 0;

      return dateB - dateA;
    });

  // Handler to export filtered orders to CSV
  const handleExportCSV = () => {
    const headers = ['Order ID', 'Order Date', 'Customer Name', 'Shipping Address', 'Products', 'Total Amount (KES)', 'Payment Status', 'Order Status'];
    
    const rows = filteredOrders.map((order: any) => {
      const totalAmount = order.orderItems?.reduce(
        (sum: number, item: any) => sum + (item.product?.sellingPrice || 0) * (item.quantity || 1),
        0
      ) || order.totalSellingPrice || 0;

      const productsList = order.orderItems?.map((item: any) => `${item.product?.title || 'Product'} (x${item.quantity})`).join('; ') || '';
      const customerName = order.shippingAddress?.name || 'N/A';
      const addressString = `${order.shippingAddress?.streetAddress || ''}, ${order.shippingAddress?.city || ''}`.replace(/"/g, '""');
      const paymentStatus = (order.paymentDetails?.paymentStatus || order.paymentStatus || 'PENDING');
      const orderDateFormatted = order.orderDate ? new Date(order.orderDate).toLocaleString() : 'N/A';

      return [
        `"#${order.id}"`,
        `"${orderDateFormatted}"`,
        `"${customerName}"`,
        `"${addressString}"`,
        `"${productsList}"`,
        totalAmount,
        `"${paymentStatus}"`,
        `"${order.orderStatus}"`
      ];
    });

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Filtered_Orders_Report_${dateFilter}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper function to build a clean printable HTML invoice string with thousand separators
  const createInvoiceHTML = (order: any) => {
    const totalAmount = order.orderItems?.reduce(
      (sum: number, item: any) => sum + (item.product?.sellingPrice || 0) * (item.quantity || 1),
      0
    ) || order.totalSellingPrice || 0;

    const orderDateFormatted = order.orderDate
      ? new Date(order.orderDate).toLocaleString()
      : new Date().toLocaleDateString();

    const paymentStatus = order.paymentDetails?.paymentStatus || order.paymentStatus;
    const mpesaRef = order.paymentDetails?.mpesaReceiptNumber || order.mpesaReceiptNumber;

    return `
      <div id="receipt-content" style="padding: 24px; font-family: Arial, sans-serif; width: 600px; background: #fff; color: #333;">
        <div style="display: flex; justify-content: space-between; border-bottom: 2px solid #333; padding-bottom: 12px; margin-bottom: 20px;">
          <div>
            <h1 style="margin: 0; font-size: 22px;">Power Tools</h1>
            <p style="margin: 4px 0 0 0; color: #666; font-size: 12px;">Order ID: #${order.id}</p>
          </div>
          <div style="text-align: right;">
            <p style="margin: 0; font-size: 12px; color: #666;">Date: ${orderDateFormatted}</p>
            <p style="margin: 4px 0 0 0; font-size: 12px; font-weight: bold;">Order Status: ${order.orderStatus}</p>
          </div>
        </div>

        <div style="margin-bottom: 20px; padding: 12px; background: #f9f9f9; border-radius: 6px; border: 1px solid #eee;">
          <h3 style="margin: 0 0 8px 0; font-size: 14px; text-transform: uppercase; color: #555;">Payment Information</h3>
          <p style="margin: 2px 0; font-size: 13px;"><strong>Till No (Buy Goods):</strong> 1588131</p>
          <p style="margin: 2px 0; font-size: 13px;"><strong>Amount:</strong> KES ${formatCurrency(totalAmount)}</p>
          <p style="margin: 2px 0; font-size: 13px;"><strong>Confirm Payment made to:</strong> POWER TOOLS</p>
          ${paymentStatus === 'COMPLETED' || mpesaRef ? `
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px dashed #ddd;">
              <p style="margin: 2px 0; font-size: 13px; color: green;"><strong>Payment Made / Details:</strong> ${mpesaRef ? `M-Pesa Ref: ${mpesaRef}` : paymentStatus}</p>
            </div>
          ` : `
            <p style="margin: 2px 0; font-size: 13px; color: #c0392b;"><strong>Payment Status:</strong> Pending Payment</p>
          `}
        </div>

        <div style="margin-bottom: 20px;">
          <h3 style="margin: 0 0 8px 0; font-size: 14px; text-transform: uppercase; color: #555;">Customer Contact</h3>
          <p style="margin: 2px 0; font-size: 13px;">0720 380 744</p>
        </div>

        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
          <thead>
            <tr style="background: #333; color: #fff;">
              <th style="padding: 8px; text-align: left; font-size: 12px;">Item</th>
              <th style="padding: 8px; text-align: center; font-size: 12px;">Qty</th>
              <th style="padding: 8px; text-align: right; font-size: 12px;">Price</th>
              <th style="padding: 8px; text-align: right; font-size: 12px;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${order.orderItems?.map((item: any) => `
              <tr style="border-bottom: 1px solid #eee;">
                <td style="padding: 8px; font-size: 12px;">${item.product?.title || 'Product'}</td>
                <td style="padding: 8px; text-align: center; font-size: 12px;">${item.quantity}</td>
                <td style="padding: 8px; text-align: right; font-size: 12px;">KES ${formatCurrency(item.product?.sellingPrice || 0)}</td>
                <td style="padding: 8px; text-align: right; font-size: 12px;">KES ${formatCurrency((item.product?.sellingPrice || 0) * (item.quantity || 1))}</td>
              </tr>
            `).join('') || ''}
          </tbody>
        </table>

        <div style="text-align: right; font-size: 16px; font-weight: bold;">
          Total Amount: KES ${formatCurrency(totalAmount)}
        </div>
      </div>
    `;
  };

  const handlePrint = (order: any) => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head><title>Order Receipt #${order.id}</title></head>
          <body onload="window.print(); window.close();">
            ${createInvoiceHTML(order)}
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const handleDownloadPDF = async (order: any) => {
    const container = document.createElement('div');
    container.innerHTML = createInvoiceHTML(order);
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    document.body.appendChild(container);

    try {
      const canvas = await html2canvas(container.firstChild as HTMLElement, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 190;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 10;

      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Order_Receipt_${order.id}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      document.body.removeChild(container);
    }
  };

  return (
    <Box className="p-4 bg-white rounded-lg shadow">
      {/* Header with Logo and Filters */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4 border-b pb-4">
        <div className="flex items-center gap-4">
          <KithimuLogo />
          <span className="text-xs text-gray-500 font-medium">Contact: 0720 380 744</span>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={filteredOrders.length === 0}
          >
            Export CSV
          </Button>
          <FormControl size="small" className="min-w-[140px]">
            <InputLabel id="time-filter-label">Filter Orders</InputLabel>
            <Select
              labelId="time-filter-label"
              value={dateFilter}
              label="Filter Orders"
              onChange={handleFilterChange}
            >
              <MenuItem value="ALL">All Time</MenuItem>
              <MenuItem value="24HRS">Last 24 Hours</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell>Order ID</StyledTableCell>
              <StyledTableCell>Products</StyledTableCell>
              <StyledTableCell>Shipping Address</StyledTableCell>
              <StyledTableCell>Payment Info</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell align="right">Update Status</StyledTableCell>
              <StyledTableCell align="right">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.map((item: any) => {
              const paymentMethod = item.paymentDetails?.paymentMethod || item.paymentMethod || 'N/A';
              const paymentStatus = item.paymentDetails?.paymentStatus || item.paymentStatus || 'PENDING';
              const mpesaRef = item.paymentDetails?.mpesaReceiptNumber || item.mpesaReceiptNumber;
              const isCompleted = paymentStatus === 'COMPLETED' || mpesaRef;

              return (
                <StyledTableRow key={item.id}>
                  <StyledTableCell component="th" scope="row">
                    #{item.id}
                  </StyledTableCell>
                  <StyledTableCell>
                    <div className="flex flex-col gap-1">
                      {item.orderItems?.map((orderItem: any, index: number) => (
                        <div key={index} className="text-sm">
                          {orderItem.product?.title} (x{orderItem.quantity})
                        </div>
                      ))}
                    </div>
                  </StyledTableCell>
                  <StyledTableCell>
                    <div className="text-xs">
                      <p className="font-bold">{item.shippingAddress?.name}</p>
                      <p>{item.shippingAddress?.streetAddress}, {item.shippingAddress?.city}</p>
                      <p>{item.shippingAddress?.mobile}</p>
                    </div>
                  </StyledTableCell>
                  <StyledTableCell>
                    <div className="flex flex-col gap-1">
                      <Chip
                        label={paymentStatus}
                        color={isCompleted ? 'success' : 'warning'}
                        size="small"
                        variant="outlined"
                        style={{ width: 'fit-content', fontWeight: 'bold' }}
                      />
                      <span className="text-xs text-gray-600">Method: {paymentMethod}</span>
                      {mpesaRef && (
                        <span className="text-xs text-gray-500">Ref: {mpesaRef}</span>
                      )}
                    </div>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Chip
                      label={item.orderStatus}
                      size="small"
                      style={{
                        backgroundColor: orderStatus.find((s) => s.label === item.orderStatus)?.color || '#ccc',
                        color: '#fff',
                        fontWeight: 'bold',
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => handleStatusClick(e, item.id)}
                    >
                      Change Status
                    </Button>
                    <Menu
                      anchorEl={anchorElStatus[item.id]}
                      open={Boolean(anchorElStatus[item.id])}
                      onClose={handleStatusClose(item.id)}
                    >
                      {orderStatus.map((status) => (
                        <MenuItem
                          key={status.label}
                          onClick={handleUpdateOrderStatus(item.id, status.label)}
                        >
                          {status.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PrintIcon />}
                      onClick={(e) => handlePrintClick(e, item.id)}
                    >
                      Receipt
                    </Button>
                    <Menu
                      anchorEl={anchorElPrint[item.id]}
                      open={Boolean(anchorElPrint[item.id])}
                      onClose={handlePrintClose(item.id)}
                    >
                      <MenuItem onClick={() => { handlePrint(item); handlePrintClose(item.id)(); }}>
                        <PrintIcon fontSize="small" style={{ marginRight: 8 }} /> Print Receipt
                      </MenuItem>
                      <MenuItem onClick={() => { handleDownloadPDF(item); handlePrintClose(item.id)(); }}>
                        <PictureAsPdfIcon fontSize="small" style={{ marginRight: 8 }} /> Download PDF
                      </MenuItem>
                    </Menu>
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}